import { FreeplayEntry, FreeplayEvent, PlayerCreateBetDTO } from "@freeplay/shared-code";
import React, { createContext, useCallback, useContext, useState } from "react";
import { waitPlease } from "../utils/pleaseWait";
import { useApplication } from "./ApplicationContext";
import { useServices } from "./ServiceContext";

type EventContextReturnMethods = {
    getEventInfo: (eventId: number) => Promise<void>;
    sendPicks: (picks: PlayerCreateBetDTO) => Promise<void>;
    setReviewState: (reviewState: boolean) => void;
    clearSubmittedScreen: () => void;
    clearEventContext: () => void;
};

type EventContextReturnProps = {
    currentEvent?: FreeplayEvent;
    currentEntry?: FreeplayEntry;
    reviewPicksState: boolean;
    showSubmittedScreen: boolean;
};

type EventContextReturn = EventContextReturnProps & EventContextReturnMethods;

const defaultEventContextProps: EventContextReturnProps = {
    reviewPicksState: false,
    showSubmittedScreen: false,
};

export const EventContext = createContext<EventContextReturn>({
    getEventInfo: () => {
        return new Promise(() => {
            // noop
        });
    },
    sendPicks: () => {
        return new Promise(() => {
            // noop
        });
    },
    setReviewState: (reviewState: boolean) => {
        // noop
    },
    clearSubmittedScreen: () => {
        // noop
    },
    clearEventContext: () => {
        // noop
    },
    ...defaultEventContextProps,
});

type EventProviderProps = {
    children?: React.ReactNode;
};

export const EventProvider = ({ children }: EventProviderProps): JSX.Element => {
    const { eventsApi, fpUser, login } = useServices();
    const { appDispatch } = useApplication();
    const [eventContextState, setEventContextState] = useState<EventContextReturnProps>({
        ...defaultEventContextProps,
    });

    const { currentEvent } = eventContextState;

    const getEventInfo = useCallback(
        async (eventId: number) => {
            const res = await eventsApi.getEvent(eventId);
            setEventContextState({
                ...eventContextState,
                currentEvent: res.event,
                currentEntry: res.entry,
            });
        },
        [eventsApi, eventContextState],
    );

    const setReviewState = useCallback(
        (reviewState: boolean) => {
            setEventContextState({
                ...eventContextState,
                reviewPicksState: reviewState,
            });
        },
        [eventContextState],
    );

    const sendPicks = useCallback(
        async (picks: PlayerCreateBetDTO) => {
            if (!currentEvent) {
                return;
            }
            appDispatch({ type: "setLoading", loading: true });
            if (!fpUser) {
                await login();
                // I do not like this.  But calls fail if we don't wait.
                await waitPlease(1);
                const updatedEventInfo = await eventsApi.getEvent(currentEvent.id);
                if (updatedEventInfo.entry) {
                    setEventContextState({
                        ...eventContextState,
                        currentEvent: updatedEventInfo.event,
                        currentEntry: updatedEventInfo.entry,
                    });
                    appDispatch({ type: "setLoading", loading: false });
                    return;
                }
            }

            const newEntry = await eventsApi.submitPicksForEvent(currentEvent.id, picks);
            setEventContextState({
                ...eventContextState,
                currentEntry: newEntry,
                reviewPicksState: false,
                showSubmittedScreen: true,
            });
            appDispatch({ type: "setLoading", loading: false });
        },
        [appDispatch, currentEvent, eventContextState, eventsApi, fpUser, login],
    );

    const clearSubmittedScreen = useCallback(() => {
        setEventContextState({ ...eventContextState, showSubmittedScreen: false });
    }, [eventContextState]);

    const clearEventContext = useCallback(() => {
        setEventContextState({
            ...defaultEventContextProps,
            currentEvent: eventContextState.currentEvent,
            currentEntry: undefined,
        });
    }, [eventContextState]);

    return (
        <EventContext.Provider
            value={{
                setReviewState,
                getEventInfo,
                sendPicks,
                clearSubmittedScreen,
                clearEventContext,
                ...eventContextState,
            }}
        >
            {children}
        </EventContext.Provider>
    );
};

export const useEvent = (): EventContextReturn => useContext(EventContext);
